/// <reference types="vite/client" />
// The spike's content (gate A1.5): every phase-A table loads from JSON under content/tables, checked against its
// JSON Schema under content/schemas at load (bad rows logged to the console, which the harness records, and
// skipped). Types come from the schemas (types.gen.ts, `pnpm --filter green-valley-spike content:gen`). Every
// player-facing string goes through `t(id, params)`; `scanRawStrings()` finds visible text that bypassed it.
// Since the composition framework's ground floor, referenced units' tables join the game's after its own (./units.ts;
// the effects table is @rpg-unit/status-presets'). The game's own values over the look presets (its `look-overrides`
// table: the overcast valley's palette, its night's and day's hour keys) are merged over the unit's `looks` and
// `daylight` rows before the registry loads them (@rpg-unit/look-presets 0.3.0, IMP-0023), so every reader sees them.
import { createRawStringScan, createStrings, DEFAULT_ALLOWED, loadContent, type ContentRegistry, type JsonSchema, type Strings, type TableFile } from "@rpg/content";
import { CONTENT_FAULTS, plantBrokenRef, RAW_STRING, ROW_CHECKS } from "./checks";
import type { ContentTables } from "./types.gen";
import { UNIT_CONTENT } from "./units";
import { applyLookOverrides } from "@rpg-unit/look-presets";

export type { ContentTables } from "./types.gen";

const TABLE_FILES = import.meta.glob<TableFile>("../../content/tables/*.json", { eager: true, import: "default" });
const SCHEMA_FILES = import.meta.glob<JsonSchema>("../../content/schemas/*.schema.json", { eager: true, import: "default" });

/** The loaded content, the strings and the scan. */
export interface SpikeContent {
  registry: ContentRegistry<ContentTables>;
  strings: Strings;
  /** Format a player-facing string. */
  t: Strings["t"];
  /** Visible text on the page that no string could have produced (empty when every screen goes through `t`). */
  scanRawStrings(root?: Element): string[];
  /** What the handle reports. */
  report(): { tables: ReturnType<ContentRegistry["tables"]>; issues: ContentRegistry["issues"] };
}

/** Load the content, planting the defects `CONTENT_FAULTS` turns on. */
export function loadSpikeContent(): SpikeContent {
  // copies: a planted defect must not leak into the bundled module's objects
  const tables = Object.keys(TABLE_FILES)
    .sort()
    .map((k) => structuredClone(TABLE_FILES[k]!));
  const schemas: Record<string, JsonSchema> = {};
  for (const [path, schema] of Object.entries(SCHEMA_FILES)) schemas[path.slice(path.lastIndexOf("/") + 1, -".schema.json".length)] = schema;
  for (const unit of UNIT_CONTENT) {
    tables.push(...unit.tables.map((t) => structuredClone(t)));
    Object.assign(schemas, unit.schemas);
  }
  if (CONTENT_FAULTS.brokenRef) plantBrokenRef(tables);
  // the game's look overrides over the unit's looks and colour script; a row that can't apply is left out and logged
  const looked = applyLookOverrides(tables);
  for (const issue of looked.issues) console.error(`content: ${issue}`);

  const registry = loadContent<ContentTables>({ tables: looked.tables, schemas, rowChecks: ROW_CHECKS });
  // phase D's story adds its own strings (quests, barks, the story screens) and the ink's lines by their ids (generated
  // from content/dialogue/*.ink): one strings table to the game, three files so the streams don't edit one
  const strings = createStrings([...registry.rows("strings"), ...registry.rows("story-strings"), ...registry.rows("dialogue-strings"), ...registry.rows("meta-strings")]);
  // numbers and punctuation pass (the default), and so does a single key glyph ("W", the rebinding screen's key caps:
  // a device's label, not prose; named keys go through the table)
  const scan = createRawStringScan(strings.messages(), { allow: [...DEFAULT_ALLOWED, /^[A-Z]$/] });
  return {
    registry,
    strings,
    t: strings.t,
    scanRawStrings: (root = document.body) => scan(visibleTexts(root)),
    report: () => ({ tables: registry.tables(), issues: registry.issues }),
  };
}

/** The planted raw string: a label put on screen without the strings table (`?faults=raw-string`). */
export function plantRawString(): HTMLElement {
  const el = document.createElement("div");
  el.textContent = RAW_STRING;
  el.style.cssText = "position:fixed;left:16px;bottom:16px;color:#fff;font:16px system-ui";
  document.body.appendChild(el);
  return el;
}

/** Every visible text node's text under `root` (one per node; hidden elements, scripts and `data-no-strings` subtrees skipped). */
export function visibleTexts(root: Element): string[] {
  const out: string[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const text = n.textContent?.trim();
    const el = n.parentElement;
    if (!text || !el) continue;
    if (el.closest("script,style,noscript,[data-no-strings]")) continue;
    if (typeof el.checkVisibility === "function" ? !el.checkVisibility({ visibilityProperty: true, opacityProperty: true }) : el.getClientRects().length === 0) continue;
    out.push(text);
  }
  return out;
}
